import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Col, Row, Spin, Alert } from "antd";
import LayoutUserLoggedOut from "../navigation/LayoutUserLoggedOut";
import logo from "../navigation/alplus-logo.png";

const API_BASE = process.env.REACT_APP_API_URI;

const maskEmail = (email) => {
  if (!email) return "";
  const atIndex = email.indexOf("@");
  if (atIndex < 0) return email;
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex);
  return `${local.slice(0, 2)}*****${domain}`;
};

const maskPhone = (phone) => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  return `******${digits.slice(-4)}`;
};

const maskPolicy = (policy) => {
  if (!policy) return "";
  return `***${policy.slice(3)}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-CA");
};

const RenewalRequestPage = () => {
  const { renewalKey } = useParams();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!renewalKey) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    fetch(`${API_BASE}/Reminder/GetRenewalRequestInfo?renewalKey=${renewalKey}`)
      .then((res) => {
        if (res.status === 404) {
          setNotFound(true);
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (!data) return;
        if (data.Unavailable) {
          setUnavailable(true);
        } else {
          setInfo(data);
        }
      })
      .catch(() => setError("Unable to load renewal information."))
      .finally(() => setLoading(false));
  }, [renewalKey]);

  const handleSubmit = () => {
    setSubmitting(true);
    fetch(`${API_BASE}/Reminder/SubmitRenewalRequest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ renewalKey: parseInt(renewalKey, 10) }),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        setDone(true);
      })
      .catch(() => setError("Something went wrong. Please try again."))
      .finally(() => setSubmitting(false));
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Row>
          <Col span={24} className="centered-box" style={{ marginTop: 36 }}>
            <Spin size="large" />
          </Col>
        </Row>
      );
    }

    if (notFound) {
      return (
        <Row style={{ marginTop: 36 }}>
          <Col span={24} className="centered-box">
            <div className="fixed-sized-box instructions-message centered-message">
              This renewal link is invalid or has expired.
            </div>
          </Col>
        </Row>
      );
    }

    if (unavailable) {
      return (
        <Row style={{ marginTop: 36 }}>
          <Col span={24} className="centered-box">
            <div className="fixed-sized-box instructions-message centered-message">
              This policy has already been renewed or cancelled and is no longer
              eligible for a renewal request.
            </div>
          </Col>
        </Row>
      );
    }

    if (error) {
      return (
        <Row justify="center" style={{ marginTop: 36 }}>
          <Col xs={24} sm={20} md={16} lg={12} xl={10}>
            <Alert message="Error" description={error} type="error" showIcon />
          </Col>
        </Row>
      );
    }

    if (done) {
      return (
        <Row style={{ marginTop: 36 }}>
          <Col span={24} className="centered-box">
            <div className="fixed-sized-box instructions-message centered-message">
              Your renewal request has been submitted. Your broker will be in
              touch shortly.
            </div>
          </Col>
        </Row>
      );
    }

    return (
      <>
        <Row style={{ marginTop: 36 }}>
          <Col span={24} className="centered-box">
            <div
              className="fixed-sized-box instructions-message centered-message"
              style={{ padding: "16px 24px", lineHeight: "1.8" }}
            >
              {info && (
                <>
                  <div>
                    Please review your details below and click{" "}
                    <strong>Request Renewal</strong> to notify your broker.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <strong>Broker:</strong> {info.BrokerName}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <strong>Policy Number:</strong> {maskPolicy(info.PolicyNumber)}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <strong>Policy Expiry:</strong>{" "}
                    {formatDate(info.PolicyExpiryDate)}
                  </div>
                  {info.PhoneNumber && (
                    <div style={{ marginTop: 4 }}>
                      <strong>Phone:</strong> {maskPhone(info.PhoneNumber)}
                    </div>
                  )}
                  {info.CustomerEmail && (
                    <div style={{ marginTop: 4 }}>
                      <strong>Email:</strong> {maskEmail(info.CustomerEmail)}
                    </div>
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>
        <Row style={{ marginTop: 36 }}>
          <Col span={24} className="centered-box">
            <Button
              type="primary"
              className="fixed-sized-box login-button"
              loading={submitting}
              onClick={handleSubmit}
            >
              Request Renewal
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <LayoutUserLoggedOut>
      <Row>
        <Col span={24}>
          <Row>
            <Col span={4} offset={10}>
              <div className="login-image-sizing">
                <img
                  className="logoImageLoading"
                  src={logo}
                  alt="AutolinkPlus logo"
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={24} className="login-sub-message">
              The preferred auto insurance management software by brokers in
              British Columbia
            </Col>
          </Row>
          <Row style={{ marginTop: 14 }}>
            <Col span={24} className="centered-box">
              <div className="tab-box-parent fixed-sized-box">
                <div className="tab-box-child">
                  <div className="tab-box-container">Renewal Request</div>
                </div>
              </div>
            </Col>
          </Row>
          {renderContent()}
        </Col>
      </Row>
    </LayoutUserLoggedOut>
  );
};

export default RenewalRequestPage;
